import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusCircle, Trash2, GripVertical, Package, DollarSign, List, Info, Image as ImageIcon } from 'lucide-react';
import { fetchItems, getAllCategories } from '../../redux/actions/InventoryAction';
import { createCostTemplate, fetchAllCostTemplates, updateCostTemplate, deleteCostTemplate } from '../../redux/actions/CostManagementActions';
import { BlindDiagram, DraggableComponent } from './ProfileReusables';
import { useSnackbarNotification } from '../order/reusables/UseSnackBarNotification';
import { getProductTypesAction } from '../../redux/actions/ProductTypeactions';
import { Switch } from '@mui/material'; 

function CostingProfileComponent() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userSignIn);
  const { data: inventoryItems, loading: itemsLoading, error: itemsError } = useSelector((state) => state.inventorytabledata);
  const { categories, loading: categoriesLoading, error: categoriesError } = useSelector((state) => state.getCategories);
  const { costTemplates, loading: templatesLoading, error: templatesError, createLoading, createError, updateLoading, updateError, deleteLoading, deleteError } = useSelector((state) => state.costTemplate);
  const { productTypes, loading: productTypesLoading, error: productTypesError } = useSelector((state) => state.productTypes);

  const { showSnackbar, SnackbarComponent } = useSnackbarNotification();

  const [availableComponents, setAvailableComponents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const maxVisiblePages = 5;
  const [currentBlindComponents, setCurrentBlindComponents] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [blindName, setBlindName] = useState('My New Blind');
  const [currentAssemblyId, setCurrentAssemblyId] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [componentEdits, setComponentEdits] = useState({});
  const [isAutoInitialize, setIsAutoInitialize] = useState(false); // Default to false

  useEffect(() => {
    if (userInfo) {
      dispatch(fetchItems(userInfo.token));
      dispatch(getAllCategories(userInfo.token));
      dispatch(fetchAllCostTemplates({ token: userInfo.token }));
      dispatch(getProductTypesAction(userInfo.token));
    }
  }, [userInfo, dispatch]);

  useEffect(() => {
    if (inventoryItems && categories) {
      console.log('Inventory Items:', inventoryItems); // Debug inventory structure
      const mappedComponents = inventoryItems.flatMap(categoryItem =>
        categoryItem.items.map(item => ({
          _id: item.id, // Use item.id as a string (assuming Firestore ID)
          name: item.name,
          category: categories.find(cat => cat.name === categoryItem.category)?.value || categoryItem.category,
          cost: parseFloat(item.sellingPrice) || 0,
          costType: item.costType || 'perUnit',
          proDependency: item.proDependency || [], // Product dependencies
        }))
      );
      console.log('Mapped Components:', mappedComponents); // Debug mapped components
      setAvailableComponents(mappedComponents);
    }
  }, [inventoryItems, categories]);

  useEffect(() => {
    const total = currentBlindComponents.reduce((sum, comp) => sum + (componentEdits[comp.instanceId]?.customCost || comp.cost || 0), 0);
    setTotalCost(total);
  }, [currentBlindComponents, componentEdits]);

  useEffect(() => {
    if (createError) showSnackbar(createError, 'error');
    if (updateError) showSnackbar(updateError, 'error');
    if (deleteError) showSnackbar(deleteError, 'error');
    if (templatesError) showSnackbar(templatesError, 'error');
    if (productTypesError) showSnackbar(productTypesError, 'error');
  }, [createError, updateError, deleteError, templatesError, productTypesError, showSnackbar]);

  useEffect(() => {
    // Only auto-initialize if auto-initialization is enabled, exactly one product type is selected, and productTypes is loaded
    if (selectedProductTypes.length === 1 && isAutoInitialize && productTypes.length > 0) {
      autoInitializeComponents();
    } else if (selectedProductTypes.length === 0 && productTypes.length > 0) {
      setCurrentBlindComponents([]);
      // Avoid showing snackbar on initial render
    }
  }, [selectedProductTypes, isAutoInitialize, productTypes]); // Added productTypes as a dependency

  const filteredComponents = availableComponents.filter(comp => {
    const categoryName = categories.find(cat => cat.value === comp.category)?.name || '';
    const searchLower = searchQuery.toLowerCase();
    return (selectedCategory === 'all' || comp.category === selectedCategory) &&
           (comp.name.toLowerCase().includes(searchLower) || categoryName.toLowerCase().includes(searchLower));
  });

  const totalPages = Math.ceil(filteredComponents.length / itemsPerPage);
  const paginatedComponents = filteredComponents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getPageRange = () => {
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    start = Math.max(1, end - maxVisiblePages + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleExternalDragStart = (e, comp) => {
    e.dataTransfer.setData("application/json", JSON.stringify(comp));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const compDataStr = e.dataTransfer.getData("application/json");
    if (!compDataStr) return;

    try {
      const dropped = JSON.parse(compDataStr);
      const newComponent = {
        _id: dropped._id, // Use the string ID
        name: dropped.name,
        instanceId: crypto.randomUUID(),
        customCost: dropped.cost,
        costType: 'sqft', // Set initial costType to sqft
      };

      setCurrentBlindComponents(prev => {
        const newComponents = [...prev];
        if (dropIndex === undefined) {
          newComponents.push(newComponent);
        } else {
          newComponents.splice(dropIndex, 0, newComponent);
        }
        return newComponents;
      });

      e.dataTransfer.clearData();
    } catch (error) {
      console.error("Error parsing dropped data:", error);
      showSnackbar("Failed to add component.", "error");
    }

    setDraggingIndex(null);
  };

  const handleDragStartComponent = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.setData("application/json", JSON.stringify(currentBlindComponents[index]));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverComponent = (e, index) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;

    setCurrentBlindComponents(prev => {
      const newComponents = [...prev];
      const [draggedItem] = newComponents.splice(draggingIndex, 1);
      newComponents.splice(index, 0, draggedItem);
      setDraggingIndex(index);
      return newComponents;
    });
  };

  const handleRemoveComponentFromBlind = (id) => setCurrentBlindComponents(prev => prev.filter(c => c.instanceId !== id));

  const handleProductTypeChange = (productTypeId) => {
    const existingProfile = costTemplates.find(t => t.productTypes.some(pt => pt._id === productTypeId));
    if (existingProfile && !selectedProductTypes.includes(productTypeId)) {
      showSnackbar(`Product type "${productTypes.find(pt => pt._id === productTypeId)?.name}" is already in use by "${existingProfile.title}".`, 'error');
      return;
    }
    setSelectedProductTypes(prev => {
      const newSelection = prev.includes(productTypeId) 
        ? [] 
        : [productTypeId]; // Enforce single selection or clear if deselecting
      return newSelection;
    });
  };

  const autoInitializeComponents = () => {
    const productTypeId = selectedProductTypes[0];
    if (!productTypeId) return;

    const selectedProductTypeName = productTypes.find(pt => pt._id === productTypeId)?.name;
    if (!selectedProductTypeName) return;
    console.log('Available Components for Auto-Init:', availableComponents); // Debug auto-init components

    const supportedComponents = availableComponents.filter(comp => 
      comp.proDependency && comp.proDependency.includes(selectedProductTypeName)
    ).map(comp => ({
      _id: comp._id, // Use the string ID
      name: comp.name,
      instanceId: crypto.randomUUID(),
      customCost: comp.cost,
      costType: 'sqft', // Set initial costType to sqft
    }));

    if (supportedComponents.length > 0) {
      setCurrentBlindComponents(supportedComponents);
      showSnackbar(`Initialized ${supportedComponents.length} components for ${selectedProductTypeName}.`, 'success');
    } else {
      setCurrentBlindComponents([]);
      showSnackbar(`No supported components found for ${selectedProductTypeName}.`, 'warning');
    }
  };

  const handleResetComponents = () => {
    setCurrentBlindComponents([]);
    showSnackbar('Components reset successfully.', 'info');
  };

  const handleSaveAssembly = () => {
    if (!blindName.trim()) {
      showSnackbar("Enter assembly name.", "error");
      return;
    }
    if (selectedProductTypes.length === 0) {
      showSnackbar("Select a product type.", "error");
      return;
    }
    if (selectedProductTypes.length > 1) {
      showSnackbar("Only one product type can be selected per profile.", "error");
      return;
    }

    // Ensure all required fields are included in the components array
    const components = currentBlindComponents.map(comp => ({
      itemId: comp._id, // Send the string ID
      customCost: componentEdits[comp.instanceId]?.customCost !== undefined 
        ? (componentEdits[comp.instanceId]?.customCost === '' ? 0 : parseFloat(componentEdits[comp.instanceId]?.customCost) || comp.customCost)
        : comp.customCost,
      costType: componentEdits[comp.instanceId]?.costType || comp.costType,
    }));

    // Log the components to debug
    console.log('Components being sent:', components);

    const totalCost = components.reduce((sum, c) => sum + (c.customCost || 0), 0);

    if (currentAssemblyId) {
      dispatch(updateCostTemplate({
        id: currentAssemblyId,
        title: blindName,
        productTypes: selectedProductTypes,
        components,
        totalCost,
        token: userInfo.token,
      })).then(() => {
        if (!updateError) showSnackbar(`Assembly "${blindName}" updated successfully!`, 'success');
      });
    } else {
      dispatch(createCostTemplate({
        title: blindName,
        productTypes: selectedProductTypes,
        components,
        totalCost,
        token: userInfo.token,
      })).then(() => {
        if (!createError) showSnackbar(`Assembly "${blindName}" saved successfully!`, 'success');
      });
    }
  };

  const handleLoadAssembly = (assemblyToLoad) => {
    setBlindName(assemblyToLoad.title);
    setSelectedProductTypes(assemblyToLoad.productTypes.map(pt => pt._id));
    setCurrentBlindComponents(assemblyToLoad.components.map(c => ({
      _id: c.itemId._id, // Use the itemId from the loaded assembly
      name: c.itemId.name, // Populate name from backend
      instanceId: crypto.randomUUID(),
      customCost: c.customCost,
      costType: c.costType || 'sqft', // Set initial costType to sqft if not present
    })));
    setTotalCost(assemblyToLoad.totalCost);
    setCurrentAssemblyId(assemblyToLoad._id);
    setComponentEdits({});
    showSnackbar(`Template "${assemblyToLoad.title}" loaded.`, 'success');
    // Ensure the loaded product type is reflected in the UI
    const loadedProductType = assemblyToLoad.productTypes[0]?._id || assemblyToLoad.productTypes[0];
    if (loadedProductType && !selectedProductTypes.includes(loadedProductType)) {
      setSelectedProductTypes([loadedProductType]);
    }
  };

  const handleNewAssembly = () => {
    setBlindName('My New Blind');
    setSelectedProductTypes([]);
    setCurrentBlindComponents([]);
    setTotalCost(0);
    setCurrentAssemblyId(null);
    setComponentEdits({});
    setIsAutoInitialize(false);
    showSnackbar('New Template started.', 'info');
  };

  const showConfirmModal = (title, message, onConfirmAction) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirmAction();
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const handleDeleteAssembly = (idToDelete, name) => {
    showConfirmModal(`Delete: ${name}`, `Delete assembly "${name}"?`, () => {
      dispatch(deleteCostTemplate({ id: idToDelete, token: userInfo.token })).then(() => {
        if (!deleteError) {
          showSnackbar(`Template "${name}" deleted.`, 'success');
          if (currentAssemblyId === idToDelete) handleNewAssembly();
        }
      });
    });
  };

  const handleDeleteComponentFromLibrary = (nameToDelete, name) => {
    showConfirmModal(`Delete: ${name}`, `Delete component "${name}" from local library?`, () => {
      setAvailableComponents(prev => prev.filter(comp => comp.name !== nameToDelete));
      showSnackbar(`Local component "${name}" deleted.`, 'success');
    });
  };

  const handleComponentEdit = (instanceId, field, value) => {
    setComponentEdits(prev => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        [field]: field === 'customCost' ? (value === '' ? '' : parseFloat(value) || 0) : value,
      },
    }));
  };

  return (
    <div className="min-vh-100 bg-white text-dark p-4">
      <SnackbarComponent />
      {confirmModal.isOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          aria-labelledby="confirmModalLabel"
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: '#ffffff' }}>
              <div className="modal-header">
                <h5 className="modal-title" id="confirmModalLabel">{confirmModal.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmModal(p => ({ ...p, isOpen: false }))}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>{confirmModal.message}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setConfirmModal(p => ({ ...p, isOpen: false }))}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmModal.onConfirm}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="mb-4">
        <h5 className="display-6 text-center text-primary">Costing Management</h5>
      </header>

      <div className="row g-4">
        <div className="col-lg-4">
          <div className="p-4 bg-white rounded shadow border border-dark">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="h5 fw-bold text-dark d-flex align-items-center mb-0">
                <List size={22} className="me-2 text-secondary"/> Available Components
              </h3>
              <select
                value={selectedCategory}
                onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
                className="form-select w-auto text-dark bg-white border-dark"
              >
                <option value="all">All Categories</option>
                {categories?.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="searchComponents" className="form-label small text-dark">Search by name or category</label>
              <input
                type="text"
                id="searchComponents"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Search components..."
                className="form-control text-dark bg-white border-dark"
              />
            </div>
            {itemsLoading || categoriesLoading || productTypesLoading ? (
              <p>Loading...</p>
            ) : itemsError || categoriesError || productTypesError ? (
              <p className="text-danger">{itemsError || categoriesError || productTypesError}</p>
            ) : paginatedComponents.length === 0 ? (
              <p>No components found.</p>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '384px' }}>
                {paginatedComponents.map((c) => (
                  <div key={c.name} className="position-relative mb-2">
                    <DraggableComponent 
                      component={c} 
                      onDragStart={(e) => handleExternalDragStart(e, c)} 
                    />
                    <button
                      onClick={() => handleDeleteComponentFromLibrary(c.name, c.name)}
                      className="position-absolute top-0 end-0 p-1 bg-danger text-white rounded-circle"
                      style={{ opacity: 0, transition: 'opacity 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <nav aria-label="Components pagination" className="mt-3">
                <ul className="pagination pagination-sm justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(1)}>First</button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Previous</button>
                  </li>
                  {currentPage > Math.floor(maxVisiblePages / 2) + 1 && (
                    <li className="page-item disabled"><span className="page-link">...</span></li>
                  )}
                  {getPageRange().map(page => (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                    </li>
                  ))}
                  {currentPage < totalPages - Math.floor(maxVisiblePages / 2) && (
                    <li className="page-item disabled"><span className="page-link">...</span></li>
                  )}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(totalPages)}>Last</button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          <div className="p-4 bg-white rounded shadow border border-dark">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
              <div className="flex-grow-1 mb-3 mb-sm-0 me-sm-4 w-100 w-sm-auto">
                <label htmlFor="blindName" className="form-label text-dark">Blind Assembly Name</label>
                <div className="input-group">
                  <input
                    type="text"
                    id="blindName"
                    value={blindName}
                    onChange={(e) => setBlindName(e.target.value)}
                    placeholder="e.g., Kitchen Roller Blind"
                    className="form-control text-dark bg-white border-dark"
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <button
                  onClick={handleSaveAssembly}
                  disabled={createLoading || updateLoading}
                  className="btn btn-success d-flex align-items-center"
                >
                  {createLoading || updateLoading ? 'Saving...' : (currentAssemblyId ? 'Update' : 'Save')}
                </button>
                <button
                  onClick={handleNewAssembly}
                  className="btn btn-warning d-flex align-items-center"
                >
                  <PlusCircle size={18} className="me-2" /> New
                </button>
                {/* MUI Switch for Auto Initialize */}
                <div className="d-flex align-items-center">
                  <Switch
                    checked={isAutoInitialize}
                    onChange={(e) => setIsAutoInitialize(e.target.checked)}
                    color="primary"
                    inputProps={{ 'aria-label': 'Auto Initialize Switch' }}
                  />
                  <span className="ms-2 text-dark">Auto Initialize</span>
                </div>
                <button
                  onClick={handleResetComponents}
                  className="btn btn-secondary d-flex align-items-center"
                  disabled={currentBlindComponents.length === 0}
                >
                  <Trash2 size={18} className="me-2" /> Reset
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label text-dark">Select Product Type</label>
              <div className="d-flex flex-wrap gap-2">
                {productTypesLoading ? (
                  <p>Loading product types...</p>
                ) : productTypesError ? (
                  <p className="text-danger">{productTypesError}</p>
                ) : productTypes.length === 0 ? (
                  <p>No product types available.</p>
                ) : (
                  productTypes.map(pt => (
                    <div key={pt._id} className="form-check">
                      <input
                        type="checkbox"
                        id={`productType-${pt._id}`}
                        checked={selectedProductTypes.includes(pt._id)}
                        onChange={() => handleProductTypeChange(pt._id)}
                        className="form-check-input"
                      />
                      <label htmlFor={`productType-${pt._id}`} className="form-check-label text-dark">
                        {pt.name}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <h3 className="h5 fw-semibold mb-3 d-flex align-items-center">
              <ImageIcon size={22} className="me-2 text-primary" /> Current Blind Configuration
            </h3>

            <div className="row">
              <div className="col-md-6">
                <div
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="border border-2 border-dashed border-dark p-4 mb-3"
                  style={{ minHeight: '300px', backgroundColor: '#f8f9fa' }}
                >
                  <h6 className="text-center mb-3 text-dark">Assembly Stack (Drag components here)</h6>
                  {currentBlindComponents.length === 0 ? (
                    <div className="text-center text-muted">
                      {selectedProductTypes.length === 0 ? 'Select a product type to auto-initialize components.' : 'No components available for selected product type.'}
                    </div>
                  ) : (
                    <div>
                      {currentBlindComponents.map((comp, index) => (
                        <div
                          key={comp.instanceId}
                          draggable
                          onDragStart={(e) => handleDragStartComponent(e, index)}
                          onDragOver={(e) => handleDragOverComponent(e, index)}
                          onDragEnd={() => setDraggingIndex(null)}
                          onDrop={(e) => handleDrop(e, index)}
                          className={`p-2 mb-2 bg-white border border-dark rounded shadow-sm d-flex align-items-center justify-content-between ${draggingIndex === index ? 'opacity-50' : ''}`}
                          style={{ minWidth: '0' }} // Ensure flex child respects width constraints
                        >
                          <div className="d-flex align-items-center flex-grow-1" style={{ maxWidth: '50%' }}>
                            <GripVertical size={16} className="me-2 text-secondary cursor-move" />
                            <Package size={14} className="me-2 text-primary" />
                            <span 
                              className="fw-medium text-dark text-truncate" 
                              style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={comp.name} // Show full name on hover
                            >
                              {comp.name}
                            </span>
                          </div>
                          <div className="d-flex align-items-center">
                            <input
                              type="number"
                              value={componentEdits[comp.instanceId]?.customCost !== undefined ? componentEdits[comp.instanceId]?.customCost : comp.customCost}
                              onChange={(e) => handleComponentEdit(comp.instanceId, 'customCost', e.target.value)}
                              className="form-control form-control-sm me-2 text-dark bg-white border-dark"
                              style={{ width: '70px' }}
                              step="0.01"
                              min="0"
                            />
                            <select
                              value={componentEdits[comp.instanceId]?.costType || comp.costType}
                              onChange={(e) => handleComponentEdit(comp.instanceId, 'costType', e.target.value)}
                              className="form-select form-select-sm me-2 text-dark bg-white border-dark"
                              style={{ width: '80px' }}
                            >
                              <option value="perUnit">Per Unit</option>
                              <option value="sqft">Sqft</option>
                              <option value="foot">Foot</option>
                            </select>
                            <button
                              onClick={() => handleRemoveComponentFromBlind(comp.instanceId)}
                              className="btn btn-sm btn-danger ms-2"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <h6 className="text-center mb-3 text-dark">Blind Preview</h6>
                <BlindDiagram components={currentBlindComponents} />
              </div>
            </div>

            <div className="mt-4 p-4 bg-white rounded shadow border border-dark">
              <h4 className="h4 fw-bold text-end text-primary">
                Total Cost: <span className="ms-2">${totalCost.toFixed(2)}</span>
              </h4>
            </div>
          </div>

          <div className="mt-4 p-4 bg-white rounded shadow border border-dark">
            <h3 className="h5 fw-semibold mb-3 d-flex align-items-center">
              <List size={22} className="me-2 text-secondary" /> Saved Assemblies
            </h3>
            {templatesLoading ? (
              <p>Loading...</p>
            ) : templatesError ? (
              <p className="text-danger">{templatesError}</p>
            ) : costTemplates.length === 0 ? (
              <p>No templates found.</p>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '240px' }}>
                {costTemplates.map(asm => {
                  const productTypeNames = asm.productTypes.map(ptId =>
                    productTypes.find(pt => pt._id === ptId._id || pt._id === ptId)?.name || 'Unknown'
                  ).join(', ');
                  return (
                    <div
                      key={asm._id}
                      className="d-flex align-items-center justify-content-between p-3 mb-2 bg-white rounded border border-dark"
                    >
                      <div>
                        <span className="fw-medium text-dark">{asm.title}</span>
                        <span className="small ms-2 text-dark">(${parseFloat(asm.totalCost).toFixed(2)})</span>
                        <div className="small text-muted">
                          Product Types: {productTypeNames || 'None'}
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          onClick={() => handleLoadAssembly(asm)}
                          className="btn btn-link text-primary p-1 d-flex align-items-center small"
                        >
                          <Info size={16} className="me-1" /> Load
                        </button>
                        <button
                          onClick={() => handleDeleteAssembly(asm._id, asm.title)}
                          disabled={deleteLoading}
                          className="btn btn-link text-danger p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="mt-5 text-center small text-dark">
        <p>© {new Date().getFullYear()} ERP Pricing Module. <a href="https://odidor.app" target="_blank" rel="noopener noreferrer">Odidor</a></p>
      </footer>
    </div>
  );
}

export default CostingProfileComponent;